// Phase C.3 — game status/result display + derivation helpers. Mirrors
// utils/teamSchedule.js's/divisionDisplay.js's role as the one place
// formatting logic lives, so pages never re-derive the same rule twice.
//
// W/L/T is NEVER stored (migration 041's own header explains why) — this
// is the one place it's computed, from the two raw scores, every time.

export const GAME_STATUS_LABELS = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  postponed: 'Postponed',
};

export const GAME_STATUS_CHIP_STYLE = {
  scheduled: { bgcolor: '#0f2136', color: '#6a8aa8' },
  completed: { bgcolor: '#0a2a14', color: '#2ea84b' },
  cancelled: { bgcolor: '#2a1010', color: '#f44336' },
  postponed: { bgcolor: '#2a1405', color: '#f5a623' },
};

export function gameStatusChipStyle(status) {
  return GAME_STATUS_CHIP_STYLE[status] || GAME_STATUS_CHIP_STYLE.scheduled;
}

/** 'W' | 'L' | 'T' | null — null whenever either score is missing (an
 * incomplete game has no result, regardless of what status claims). */
export function deriveGameResult(teamScore, opponentScore) {
  if (teamScore === null || teamScore === undefined || opponentScore === null || opponentScore === undefined) {
    return null;
  }
  if (teamScore > opponentScore) return 'W';
  if (teamScore < opponentScore) return 'L';
  return 'T';
}

/** Team record derived ENTIRELY from completed games — never a stored,
 * independently-editable field (see this phase's own explicit rule).
 * Cancelled/postponed/scheduled games never affect it, since only
 * status === 'completed' rows are counted at all. Returns null `display`
 * when there are zero completed games — callers should show "No results
 * yet" rather than a misleading "0-0" (per the phase brief's own
 * guidance), not this function's job to pick that copy. */
export function deriveTeamRecord(games) {
  let wins = 0, losses = 0, ties = 0;
  for (const g of games || []) {
    if (g.status !== 'completed') continue;
    const result = deriveGameResult(g.team_score, g.opponent_score);
    if (result === 'W') wins += 1;
    else if (result === 'L') losses += 1;
    else if (result === 'T') ties += 1;
  }
  const played = wins + losses + ties;
  const display = played === 0 ? null : (ties > 0 ? `${wins}-${losses}-${ties}` : `${wins}-${losses}`);
  return { wins, losses, ties, played, display };
}

/** "vs Jefferson" (home/neutral) or "@ Watertown" (away) — neutral gets
 * an explicit "· Neutral" suffix so it's never confused with a home
 * game just because both start with "vs". */
export function formatGameOpponentLine(game) {
  const opponent = game.opponent_name || 'Opponent';
  if (game.home_away === 'away') return `@ ${opponent}`;
  if (game.home_away === 'neutral') return `vs ${opponent} · Neutral`;
  return `vs ${opponent}`;
}

/** "W 24–14" — completed games only; callers should not call this for a
 * game with no derivable result (check deriveGameResult first). */
export function formatGameScoreLine(game) {
  const result = deriveGameResult(game.team_score, game.opponent_score);
  if (!result) return '';
  return `${result} ${game.team_score}–${game.opponent_score}`;
}

// Never pass event_date through `new Date(dateString)` directly — see
// utils/teamSchedule.js's own comment on this exact bug (a bare ISO date
// parses as UTC midnight, which can display as the previous day west of
// UTC). Same fix, applied locally here rather than importing a private
// helper across files.
function parseLocalDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** "Aug 28" — compact, no weekday (unlike utils/teamSchedule.js's own
 * formatEventDate(), which is for the fuller private Schedule page). */
export function formatGameDateCompact(dateStr) {
  return parseLocalDate(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "Aug 23, 2026" — the Division Hub's Results ledger date-group header.
 * Includes the year (unlike formatGameDateCompact()) since a ledger is
 * more likely to be read well after the fact. */
export function formatResultDateLabel(dateStr) {
  return parseLocalDate(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "TODAY" (exact string match against `todayStr`, both 'YYYY-MM-DD' —
 * see utils/teamSchedule.js's todayDateString(), the one place "today"
 * is computed, so this never re-derives it independently) or
 * "Tuesday, September 1" for the Division Hub's Scheduled date-group
 * headers — TODAY must be obvious without relying on color alone. */
export function formatScheduleDayLabel(dateStr, todayStr) {
  if (dateStr === todayStr) return 'TODAY';
  return parseLocalDate(dateStr).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}
